/* eslint-disable max-lines-per-function */
import { ConfirmDeleteDialog } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/components/ConfirmDeleteDialog';
import { useDeleteVorlageAbschnitt } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useDeleteVorlageAbschnitt/useDeleteVorlageAbschnitt';
import type { VorlageAbschnittDetails } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useRotationsplanVorlageById/useRotationsplanVorlageById';
import { toast } from '@/hooks/use-toast';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@e2n/ui/v2';
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Sentry from '@sentry/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const TRANSLATION_PREFIX =
  'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteAbschnitt';

export function DeleteVorlageAbschnitt({
  vorlageId,
  abschnitt,
  istLehrjahr,
}: {
  vorlageId: string;
  abschnitt: VorlageAbschnittDetails;
  istLehrjahr: boolean;
}) {
  const { t } = useTranslation();
  const [deleteVorlageAbschnitt] = useDeleteVorlageAbschnitt();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const art = istLehrjahr ? 'lehrjahr' : 'abschnitt';

  async function handleDelete() {
    setIsSubmitting(true);

    try {
      const result = await deleteVorlageAbschnitt({
        vorlageAbschnittId: abschnitt.id,
        vorlageId,
        position: abschnitt.position,
      });

      if (!result.data?.mitarbeiter_deleteVorlageAbschnitt.success) {
        toast({
          variant: 'destructive',
          title: t(`${TRANSLATION_PREFIX}.error.${art}`),
        });
        return;
      }

      setIsDialogOpen(false);
      toast({ title: t(`${TRANSLATION_PREFIX}.success.${art}`) });
    } catch (error) {
      Sentry.captureException(error);
      toast({
        variant: 'destructive',
        title: t(`${TRANSLATION_PREFIX}.error.${art}`),
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              aria-label={t(`${TRANSLATION_PREFIX}.tooltip`)}
              disabled={isSubmitting}
              iconOnly
              variant="secondary-red"
              onClick={() => {
                // ohne Stationen geht nichts verloren, wie beim Rotationsplan
                if (abschnitt.stationen.length === 0) {
                  handleDelete();
                } else {
                  setIsDialogOpen(true);
                }
              }}>
              <FontAwesomeIcon icon={faTrashCan} className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            {t(`${TRANSLATION_PREFIX}.tooltip`)}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <ConfirmDeleteDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        title={t(`${TRANSLATION_PREFIX}.title`, {
          name: `${abschnitt.position}. ${abschnitt.name}`,
        })}
        description={t(`${TRANSLATION_PREFIX}.subtitle.${art}`)}
        isSubmitting={isSubmitting}
        onConfirm={handleDelete}
      />
    </>
  );
}
